/**
 * Chat — chat window for 1-on-1, group, and AI buddy conversations.
 * Renders message bubbles, input bar with reply/edit support, and message
 * actions (reply, edit, delete) via a popover menu.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { resolveComposerDraftForReply } from '@wlb/shared/utils/chatReplySelection';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useMessages } from '@wlb/shared/context/MessageContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { isAiConversationType, isGroupConversationType } from '@wlb/shared/constants';
import {
  canDeleteChatMessage,
  canEditChatMessage,
  isChatMessageSentByUser,
} from '@wlb/shared/utils/chatMessageActions';
import { enrichMessagesWithReplyContext } from '@wlb/shared/utils/chatReplyPreview';
import type { Conversation, ExtendedChatMessage } from '@wlb/shared/types/chat';
import type { SendMessageResult } from '@wlb/shared/types/context';
import type { GifSelection } from '@wlb/shared/types/gif';
import { ChevronLeftIcon, PhoneIcon, VideoCallIcon, InfoIcon, PencilIcon, TrashIcon } from '../components/ui/icons';
import { avatarFallback } from '../components/ui/avatarFallback';
import { ChatBubble } from '../features/messages/ChatBubble';
import { GroupAvatar } from '../features/messages/GroupAvatar';
import { resolveAvatarSrc } from '../features/messages/avatarSource';
import { resolvePeerUserGuid } from '../features/messages/resolvePeerUserGuid';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import { useConversationNotificationsRead } from '../features/notifications/useConversationNotificationsRead';
import { buildChatTimeline } from '../features/messages/chatTimeline';
import { resolveChatReturn } from '../features/messages/chatNavigation';
import { ChatDaySeparator } from '../features/messages/ChatDaySeparator';
import { ChatInput } from '../features/messages/ChatInput';
import { ChatSkeleton } from '../features/messages/ChatSkeleton';
import { ModerationHoldNotice } from '../features/moderation/ModerationHoldNotice';
import { PopoverMenu, type PopoverMenuItem } from '../components/ui/PopoverMenu';
import { Button } from '../components/ui/Button';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import '../components/ui/avatar.css';
import '../features/messages/messages.css';

type ActionKey = 'edit' | 'delete';

/** Empty when unnamed — callers substitute the translated fallback. */
function resolveDisplayName(conversation: Conversation | null): string {
  if (!conversation) return '';
  if (isGroupConversationType(conversation.conversationType)) {
    // A group title is not a person's name, so it is never shortened.
    return conversation.groupName || conversation.name || '';
  }
  return getUsernameDisplay(conversation);
}

export function Chat() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { userId } = useAuth();
  const {
    messages,
    messagesLoading,
    messagesError,
    sending,
    replyTo,
    moderationHold,
    clearModerationHold,
    getConversationById,
    ensureConversationsLoaded,
    selectConversation,
    fetchMessages,
    markConversationRead,
    syncActiveMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    setReplyTo,
    getParticipants,
  } = useMessages();
  const openUserProfile = useOpenUserProfile();

  const [inputValue, setInputValue] = useState('');
  const [resolvingPeer, setResolvingPeer] = useState(false);
  const [editingMessage, setEditingMessage] = useState<ExtendedChatMessage | null>(null);
  const [actionAnchorRect, setActionAnchorRect] = useState<DOMRect | null>(null);
  const [actionMessage, setActionMessage] = useState<ExtendedChatMessage | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const conversation = useMemo(
    () => getConversationById(conversationId || ''),
    [getConversationById, conversationId],
  );

  const isAiChat = isAiConversationType(conversation?.conversationType);
  const isGroup = isGroupConversationType(conversation?.conversationType);
  const displayName = resolveDisplayName(conversation);
  const headerTitle = displayName || t(isGroup ? 'chat.unnamedGroup' : 'chat.defaultTitle');
  const avatarSrc = resolveAvatarSrc(conversation?.avatar);
  const currentUserId = userId ?? '';
  const headerFallback = avatarFallback({
    seed: conversation?.peerUserGuid ?? conversation?.peerUserId ?? conversation?.id,
    name: headerTitle,
    username: conversation?.username,
  });

  // Messages arrive newest-first, so this is already the recent-sender order.
  const recentSenderIds = useMemo(
    () => (isGroup ? messages.map(message => message.user?._id).filter(Boolean) as Array<string | number> : []),
    [isGroup, messages],
  );

  // Figma shows the name alone for 1-on-1 chats; the avatar dot carries presence.
  const statusText = isAiChat
    ? t('robi.quickAnswer')
    : isGroup
      ? `${conversation?.memberCount || 0} ${t('createGroupChat.members')}`
      : '';

  // Robi / Ask Anything threads have their own screen and reject sends from here.
  useEffect(() => {
    if (isAiChat) navigate('/robi', { replace: true });
  }, [isAiChat, navigate]);

  // The header, the group/AI branches and the avatar all read the list row, so
  // a chat opened before the inbox was ever fetched pulls the list once.
  useEffect(() => {
    if (!conversationId || conversation) return;
    ensureConversationsLoaded().catch(() => undefined);
  }, [conversationId, conversation, ensureConversationsLoaded]);

  // Load messages on mount / conversation change
  useEffect(() => {
    if (!conversationId || isAiChat) return;
    selectConversation(conversationId);
    fetchMessages(conversationId).catch(() => undefined);
    // Reaching the thread is what clears it, however the member got here. The
    // inbox marks the row it opens, but a deep link or a reload inside the
    // thread never passes through the inbox; the receipt is de-duplicated per
    // thread, so the two paths together still send at most one.
    markConversationRead(conversationId).catch(() => undefined);
  }, [conversationId, isAiChat, selectConversation, fetchMessages, markConversationRead]);

  // Reading the thread also settles its notifications — the bell should not
  // keep listing messages that are already on screen.
  useConversationNotificationsRead(conversationId, !isAiChat);

  // Poll for new messages every 4 seconds (mobile parity)
  useEffect(() => {
    if (!conversationId) return;
    syncIntervalRef.current = setInterval(() => {
      syncActiveMessages(conversationId).catch(() => undefined);
    }, 4000);
    return () => {
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    };
  }, [conversationId, syncActiveMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    // Rephrasing is the fix for a hold, so the guidance goes away as soon as
    // the user starts rewriting rather than lingering over the new draft.
    clearModerationHold();
  }, [clearModerationHold]);

  const handleSend = useCallback(async () => {
    if (!conversationId || !inputValue.trim() || sending) return;
    const text = inputValue.trim();
    setInputValue('');

    if (editingMessage) {
      try {
        await editMessage(conversationId, editingMessage._id, text);
      } catch {
        setInputValue(text);
        ToastService.show({ type: 'error', message: t('chat.errors.editFailed') });
        return;
      }
      // A moderation hold is reported by the inline notice below, which reads
      // the context state, so the editor closes either way.
      setEditingMessage(null);
      return;
    }

    // sendMessage never throws — it reports a rejection in its result.
    let result: SendMessageResult = { message: null, heldForReview: false };
    try {
      result = await sendMessage(conversationId, {
        text,
        replyToMessageId: replyTo?._id ?? null,
      });
    } catch {
      result = { message: null, heldForReview: false };
    }

    // A held message was persisted and the thread was resynced to show it with
    // an "under review" badge, so putting the text back would duplicate it.
    if (!result.message && !result.heldForReview) {
      setInputValue(text);
      ToastService.show({ type: 'error', message: t('chat.errors.sendFailed') });
    }
  }, [conversationId, inputValue, sending, editingMessage, editMessage, sendMessage, replyTo, t]);

  const handleLongPress = useCallback((message: ExtendedChatMessage, rect: DOMRect) => {
    setActionMessage(message);
    setActionAnchorRect(rect);
  }, []);

  const handleReply = useCallback((message: ExtendedChatMessage) => {
    setInputValue(current =>
      resolveComposerDraftForReply(current, editingMessage !== null),
    );
    setEditingMessage(null);
    setReplyTo(message);
  }, [editingMessage, setReplyTo]);

  const handleActionSelect = useCallback((key: ActionKey) => {
    setActionAnchorRect(null);
    if (!actionMessage) return;

    if (key === 'edit' && canEditChatMessage(actionMessage, currentUserId)) {
      setEditingMessage(actionMessage);
      setInputValue(actionMessage.text || '');
      setActionMessage(null);
    } else if (key === 'delete' && canDeleteChatMessage(actionMessage, currentUserId)) {
      setDeleteConfirmOpen(true);
    }
  }, [actionMessage, currentUserId]);

  const handleConfirmDelete = useCallback(async () => {
    if (!conversationId || !actionMessage || deleting) return;
    setDeleting(true);
    try {
      await deleteMessage(conversationId, actionMessage._id);
    } catch {
      ToastService.show({ type: 'error', message: t('chat.errors.deleteFailed') });
    }
    setDeleting(false);
    setDeleteConfirmOpen(false);
    setActionMessage(null);
  }, [conversationId, actionMessage, deleting, deleteMessage, t]);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
    setActionMessage(null);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyTo(null);
  }, [setReplyTo]);

  const openDetails = useCallback(() => {
    if (!conversationId) return;
    navigate(`/messages/${conversationId}/details`);
  }, [conversationId, navigate]);

  /**
   * The header identity opens the person, not the thread — chat settings stay
   * one tap away behind the info button. Group headers have no single profile
   * to open, so they keep going to details.
   */
  const openPeerProfile = useCallback(async () => {
    if (resolvingPeer) return;
    setResolvingPeer(true);
    const guid = await resolvePeerUserGuid(
      conversation,
      currentUserId ? String(currentUserId) : null,
      getParticipants,
    );
    setResolvingPeer(false);
    if (!guid) {
      ToastService.show({ type: 'error', message: t('profile.toast.unableToLoadProfile') });
      return;
    }
    openUserProfile(guid);
  }, [resolvingPeer, conversation, currentUserId, getParticipants, openUserProfile, t]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
    setInputValue('');
  }, []);

  const handleSendGif = useCallback(async (gif: GifSelection) => {
    if (!conversationId || sending) return;
    try {
      const result = await sendMessage(conversationId, {
        imageUrl: gif.uri,
        fileName: gif.fileName,
        fileSizeBytes: gif.fileSize,
      });
      if (!result.message && !result.heldForReview) {
        ToastService.show({ type: 'error', message: t('chat.errors.sendFailed') });
      }
    } catch {
      ToastService.show({ type: 'error', message: t('chat.errors.sendFailed') });
    }
  }, [conversationId, sending, sendMessage, t]);

  // Edit and Delete are offered only while the API would accept them, so an
  // action never appears and then fails on a message it cannot address.
  const actionItems = useMemo<PopoverMenuItem<ActionKey>[]>(() => {
    if (!actionMessage) return [];
    const items: PopoverMenuItem<ActionKey>[] = [];
    if (canEditChatMessage(actionMessage, currentUserId)) {
      items.push({ key: 'edit', label: t('individualChat.actions.edit'), icon: PencilIcon });
    }
    if (canDeleteChatMessage(actionMessage, currentUserId)) {
      items.push({ key: 'delete', label: t('individualChat.actions.delete'), icon: TrashIcon, destructive: true });
    }
    return items;
  }, [actionMessage, currentUserId, t]);

  const displayMessages = useMemo(
    () =>
      enrichMessagesWithReplyContext(messages, {
        peerDisplayName: isGroup ? null : headerTitle,
        selfDisplayName: t('chat.me'),
        currentUserId,
      }),
    [messages, isGroup, headerTitle, currentUserId, t],
  );
  const renderItems = useMemo(
    () => buildChatTimeline(displayMessages),
    [displayMessages],
  );
  const returnLocation = resolveChatReturn(location.state);

  const handleBack = useCallback(() => {
    if (returnLocation) {
      navigate(returnLocation.returnTo, {
        replace: true,
        state: returnLocation.returnState,
      });
      return;
    }
    navigate('/messages');
  }, [navigate, returnLocation]);

  return (
    <section className="chat-page" aria-label={headerTitle}>
      <div className="chat-header">
        <div className="chat-header__left">
          <button
            type="button"
            className="chat-header__back"
            onClick={handleBack}
            aria-label={t('home.notifications.accessibility.back')}
          >
            <ChevronLeftIcon size={24} />
          </button>
          <button
            type="button"
            className="chat-header__identity"
            onClick={isGroup ? openDetails : () => { openPeerProfile().catch(() => undefined); }}
            aria-busy={resolvingPeer}
            aria-label={
              isGroup
                ? t('chat.accessibility.viewChatDetails', { name: headerTitle })
                : t('home.accessibility.openUserProfile', { username: headerTitle })
            }
          >
            <div className="chat-header__avatar-wrap">
              {isGroup ? (
                <GroupAvatar
                  name={headerTitle}
                  avatar={conversation?.avatar}
                  members={conversation?.members}
                  recentSenderIds={recentSenderIds}
                  variant="header"
                />
              ) : avatarSrc ? (
                <img src={avatarSrc} className="chat-header__avatar" alt="" aria-hidden />
              ) : (
                <div className="chat-header__avatar-fallback" data-avatar-tone={headerFallback.tone} aria-hidden>
                  {headerFallback.initial}
                </div>
              )}
              {conversation?.isOnline && <div className="chat-header__online-dot" aria-hidden />}
            </div>
            <div className="chat-header__info">
              <span className="chat-header__name">{headerTitle}</span>
              {statusText && <span className="chat-header__status">{statusText}</span>}
            </div>
          </button>
        </div>
        <div className="chat-header__actions">
          <button
            type="button"
            className="chat-header__action-btn chat-header__action-btn--disabled"
            disabled
            aria-label={t('chat.accessibility.audioCall')}
          >
            <PhoneIcon size={24} />
          </button>
          <button
            type="button"
            className="chat-header__action-btn chat-header__action-btn--disabled"
            disabled
            aria-label={t('chat.accessibility.videoCall')}
          >
            <VideoCallIcon size={24} />
          </button>
          <button
            type="button"
            className="chat-header__action-btn"
            onClick={openDetails}
            aria-label={t('chatProfileDetails.chatSettings')}
          >
            <InfoIcon size={24} />
          </button>
        </div>
      </div>

      {messagesError && messages.length === 0 && !messagesLoading ? (
        <div className="chat-error">
          <p className="chat-error__text">{t('chat.errors.loadMessagesFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => conversationId && fetchMessages(conversationId).catch(() => undefined)}>
            {t('chat.retry')}
          </Button>
        </div>
      ) : messagesLoading && messages.length === 0 ? (
        <ChatSkeleton />
      ) : (
        <div className="chat-messages" role="log" aria-live="polite">
          {renderItems.map(item => {
            if (item.type === 'day') {
              return <ChatDaySeparator key={item.key} date={item.date} />;
            }
            const message = item.message;
            const isSent = isChatMessageSentByUser(message, currentUserId);
            const hasMoreActions =
              canEditChatMessage(message, currentUserId) ||
              canDeleteChatMessage(message, currentUserId);
            return (
              <ChatBubble
                key={item.key}
                message={message}
                isSent={isSent}
                isGroup={isGroup}
                onLongPress={hasMoreActions ? handleLongPress : undefined}
                onReply={handleReply}
              />
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      )}

      {moderationHold && moderationHold.conversationId === conversationId && (
        <ModerationHoldNotice
          hold={moderationHold}
          onDismiss={clearModerationHold}
        />
      )}

      <ChatInput
        value={inputValue}
        onChange={handleInputChange}
        onSend={handleSend}
        sending={sending}
        replyTo={replyTo}
        editingMessage={editingMessage}
        onCancelReply={handleCancelReply}
        onCancelEdit={handleCancelEdit}
        onSendGif={handleSendGif}
      />

      <PopoverMenu
        anchorRect={actionAnchorRect}
        items={actionItems}
        label={t('chat.accessibility.messageActions')}
        onClose={() => setActionAnchorRect(null)}
        onSelect={handleActionSelect}
      />

      {deleteConfirmOpen && (
        <ConfirmModal
          title={t('chat.deleteConfirmTitle')}
          message={t('chat.deleteConfirmMessage')}
          confirmLabel={t('chat.deleteConfirm')}
          cancelLabel={t('chat.deleteCancel')}
          showIcon
          loading={deleting}
          onClose={closeDeleteConfirm}
          onConfirm={() => { handleConfirmDelete().catch(() => undefined); }}
        />
      )}
    </section>
  );
}
